const LEGACY_APP_TOKEN_STORAGE_KEY = "token";
const SUPABASE_BRIDGE_TOKEN_STORAGE_KEY = "supabase.bridge.access_token";
const ACTIVE_AUTH_TOKEN_SOURCE_STORAGE_KEY = "auth.active_token_source";
const AUTH_TRANSITION_BLOCK_STORAGE_KEY = "auth.transition_blocked";
const SUPABASE_URL_AUTH_FIELDS = [
  "access_token",
  "refresh_token",
  "expires_in",
  "expires_at",
  "token_type",
  "type",
  "provider_token",
  "provider_refresh_token",
];

function getWindowObject() {
  return typeof window !== "undefined" ? window : null;
}

function normalizeToken(value) {
  const normalized = (value || "").trim();
  return normalized || null;
}

function isAuthTransitionBlocked(win = getWindowObject()) {
  if (!win) return false;
  return win.sessionStorage.getItem(AUTH_TRANSITION_BLOCK_STORAGE_KEY) === "1";
}

function readLegacyToken(win = getWindowObject()) {
  if (!win) return null;
  return normalizeToken(win.localStorage.getItem(LEGACY_APP_TOKEN_STORAGE_KEY));
}

function readStoredSupabaseBridgeToken(win = getWindowObject()) {
  if (win) {
    win.sessionStorage.removeItem(SUPABASE_BRIDGE_TOKEN_STORAGE_KEY);
  }
  return null;
}

function parseSupabaseAccessTokenFromUrl(win = getWindowObject()) {
  if (!win) return null;

  const currentUrl = new URL(win.location.href);
  const searchToken = normalizeToken(currentUrl.searchParams.get("access_token"));
  if (searchToken) {
    return {
      token: searchToken,
      source: "search",
    };
  }

  const hashValue = currentUrl.hash.startsWith("#")
    ? currentUrl.hash.slice(1)
    : currentUrl.hash;
  if (!hashValue || !hashValue.includes("access_token")) {
    return null;
  }

  const hashParams = new URLSearchParams(hashValue);
  const hashToken = normalizeToken(hashParams.get("access_token"));
  if (!hashToken) {
    return null;
  }

  // Recovery tokens are for password reset only. Exclude them from session
  // bootstrap so the URL hash is preserved for ResetPassword to read directly.
  const tokenType = (hashParams.get("type") || "").trim();
  if (tokenType === "recovery") {
    return null;
  }

  return {
    token: hashToken,
    source: "hash",
  };
}

function sanitizeUrlAuthFields(win = getWindowObject()) {
  if (!win) return;

  const currentUrl = new URL(win.location.href);
  let changed = false;

  for (const fieldName of SUPABASE_URL_AUTH_FIELDS) {
    if (currentUrl.searchParams.has(fieldName)) {
      currentUrl.searchParams.delete(fieldName);
      changed = true;
    }
  }

  const hashValue = currentUrl.hash.startsWith("#")
    ? currentUrl.hash.slice(1)
    : currentUrl.hash;
  if (hashValue && hashValue.includes("access_token")) {
    const hashParams = new URLSearchParams(hashValue);
    for (const fieldName of SUPABASE_URL_AUTH_FIELDS) {
      if (hashParams.has(fieldName)) {
        hashParams.delete(fieldName);
        changed = true;
      }
    }

    const nextHash = hashParams.toString();
    currentUrl.hash = nextHash ? `#${nextHash}` : "";
  }

  if (!changed) {
    return;
  }

  win.history.replaceState({}, win.document.title, currentUrl.toString());
}

function captureSupabaseBridgeToken(win = getWindowObject()) {
  if (!win) return readStoredSupabaseBridgeToken(win);

  const urlToken = parseSupabaseAccessTokenFromUrl(win);
  if (urlToken?.token) {
    sanitizeUrlAuthFields(win);
    return urlToken.token;
  }

  return readStoredSupabaseBridgeToken(win);
}

export function hasAvailableBootstrapToken(win = getWindowObject()) {
  if (!win) return false;
  if (isAuthTransitionBlocked(win)) return false;

  return Boolean(
    readLegacyToken(win) ||
      readStoredSupabaseBridgeToken(win) ||
      parseSupabaseAccessTokenFromUrl(win)?.token
  );
}

export function getLegacyAppToken(win = getWindowObject()) {
  return readLegacyToken(win);
}

export function getActiveAuthTokenSource(win = getWindowObject()) {
  if (win) {
    win.sessionStorage.removeItem(ACTIVE_AUTH_TOKEN_SOURCE_STORAGE_KEY);
  }
  return null;
}

export function setActiveAuthTokenSource(source, win = getWindowObject()) {
  if (!win) return;

  win.sessionStorage.removeItem(ACTIVE_AUTH_TOKEN_SOURCE_STORAGE_KEY);
}

export function getAuthBootstrapTokenCandidates(win = getWindowObject()) {
  if (!win) return [];
  if (isAuthTransitionBlocked(win)) return [];

  const legacyToken = readLegacyToken(win);
  const supabaseToken = captureSupabaseBridgeToken(win);
  const candidates = [];

  if (legacyToken) {
    candidates.push({
      source: "legacy",
      token: legacyToken,
    });
  }

  if (supabaseToken && supabaseToken !== legacyToken) {
    candidates.push({
      source: "supabase",
      token: supabaseToken,
    });
  }

  return candidates;
}

export function getPreferredAuthToken(win = getWindowObject()) {
  return getAuthBootstrapTokenCandidates(win)[0]?.token || null;
}

export function getActiveAwareAuthToken(win = getWindowObject()) {
  if (!win) return null;
  if (isAuthTransitionBlocked(win)) return null;

  return getPreferredAuthToken(win);
}

export function clearBootstrapTokenForSource(source, win = getWindowObject()) {
  if (!win) return;

  if (source === "legacy") {
    win.localStorage.removeItem(LEGACY_APP_TOKEN_STORAGE_KEY);
    win.sessionStorage.removeItem(ACTIVE_AUTH_TOKEN_SOURCE_STORAGE_KEY);
    return;
  }

  if (source === "supabase") {
    win.sessionStorage.removeItem(SUPABASE_BRIDGE_TOKEN_STORAGE_KEY);
    win.sessionStorage.removeItem(ACTIVE_AUTH_TOKEN_SOURCE_STORAGE_KEY);
  }
}

export function clearAllBootstrapTokens(win = getWindowObject()) {
  if (!win) return;

  win.localStorage.removeItem(LEGACY_APP_TOKEN_STORAGE_KEY);
  win.sessionStorage.removeItem(SUPABASE_BRIDGE_TOKEN_STORAGE_KEY);
  win.sessionStorage.removeItem(ACTIVE_AUTH_TOKEN_SOURCE_STORAGE_KEY);
  win.sessionStorage.removeItem(AUTH_TRANSITION_BLOCK_STORAGE_KEY);
}

export function shouldDiscardBootstrapTokenAfterValidationFailure(status) {
  return Number.isInteger(status) && status >= 400 && status < 500;
}

export function setAuthTransitionBlocked(blocked, win = getWindowObject()) {
  if (!win) return;

  if (blocked) {
    win.sessionStorage.setItem(AUTH_TRANSITION_BLOCK_STORAGE_KEY, "1");
    return;
  }

  win.sessionStorage.removeItem(AUTH_TRANSITION_BLOCK_STORAGE_KEY);
}
